import React, { Dispatch, SetStateAction, useState } from 'react';
import {
  View, StyleSheet, Text, Pressable,
} from 'react-native';
import { useSelector } from 'react-redux';
import Style from '../../../constants/Style';
import Colors from '../../../constants/Colors';
import AddBuildingModal, { Building } from './AddBuildingModal';
import deleteBuilding from '../../../requests/Locations/Buildings/DeleteBuilding';
import { UserPermission } from '../../../constants/Types';
import {
  Area, Floor, LocationSubtypeTitles, Space,
} from '../../../components/Map/SetFloorsModal/FloorItem';
import PressableStyled, { PressableState } from '../../../components/PressableStyled';
import DropdownIcon12 from '../../../components/12pxIcons/DropdownIcon12';
import { logEvent } from '../../../components/LogEvent';
import EllipsisIcon from '../../../components/EllipsisIcon';

interface BuildingMenuItemProps {
  building: Building,
  expandedBuildingId: string,
  expandedFloorId: string,
  expandedAreaId: string,
  selectedBuilding: Building | null,
  selectedFloor: Floor | null,
  selectedArea: Area | null,
  selectedSpace: Space | null,
  buildingMenuShownId: string,
  setSelectedBuildingId: Dispatch<SetStateAction<string>>,
  setSelectedFloorId: Dispatch<SetStateAction<string>>,
  setSelectedAreaId: Dispatch<SetStateAction<string>>,
  setSelectedSpaceId: Dispatch<SetStateAction<string>>,
  setExpandedBuildingId: Dispatch<SetStateAction<string>>,
  setExpandedFloorId: Dispatch<SetStateAction<string>>,
  setExpandedAreaId: Dispatch<SetStateAction<string>>,
  setBuildingMenuShownId: Dispatch<SetStateAction<string>>,
  setLoading: Dispatch<SetStateAction<boolean>>,
  showScreenAlert: Function,
  removeBuilding: Function,
  editBuilding: Function,
  index: number,
  checkIfNameUnique,
  testID?: string,
}

const BuildingMenuItem = ({
  building, expandedBuildingId, selectedBuilding, selectedFloor, buildingMenuShownId, index,
  setSelectedBuildingId, setSelectedFloorId, setExpandedBuildingId, setBuildingMenuShownId,
  showScreenAlert, removeBuilding, editBuilding, setLoading, expandedFloorId, setExpandedFloorId,
  selectedArea, setSelectedAreaId, expandedAreaId, setExpandedAreaId, selectedSpace,
  setSelectedSpaceId, checkIfNameUnique, testID,
}: BuildingMenuItemProps) => {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const [addBuildingModalShown, setAddBuildingModalShown] = useState<boolean>(false);
  const canUpdateLocations: boolean = isAdmin
    || permissions.includes(UserPermission.LOCATIONS_UPDATE);
  const canDeleteLocations: boolean = isAdmin
    || permissions.includes(UserPermission.LOCATIONS_DELETE);

  if (!building) return null;
  const buildingExpanded: boolean = building.LocationId === expandedBuildingId;
  const buildingSelected: boolean = building.LocationId === selectedBuilding?.LocationId;
  const menuShown: boolean = buildingMenuShownId === building.LocationId;

  const onEditPress: () => void = () => {
    setBuildingMenuShownId(null);
    setAddBuildingModalShown(true);
  };

  const onDeletePress: () => void = () => {
    setBuildingMenuShownId(null);
    showScreenAlert(
      'Delete building?',
      <Text>
        {'Are you sure you would like to delete '}
        <Text style={Style.alertBoldText}>{building.Name}</Text>
        {'?'}
      </Text>,
      true,
      () => {
        setLoading(true);
        deleteBuilding(building.LocationId).then((res) => {
          if (res && !res.isError) {
            removeBuilding(building.LocationId);
            showScreenAlert(
              'Deleted building successfully',
              <Text>
                <Text style={Style.alertBoldText}>{building.Name}</Text>
                {' was successfully deleted'}
              </Text>,
              false,
            );
          } else deleteError(res?.errorText);
        }).catch((error) => deleteError(error?.message));
      },
      'Delete',
    );
  };

  const deleteError = (errorText?: string) => showScreenAlert(
    'Error',
    errorText ?? 'Could not delete building at the moment. Please try again later',
  );

  const hideAddBuildingModal: () => void = () => setAddBuildingModalShown(false);

  return <View
    style={[
      styles.container,
      { zIndex: index * -1 },
      buildingExpanded && buildingSelected && styles.selected,
    ]}
    {...{ testID }}
  >
    <Pressable
      style={[Style.row, styles.header, buildingSelected && styles.selected]}
      onPress={() => {
        setSelectedBuildingId(building?.LocationId);
        setSelectedFloorId(null);
        setSelectedAreaId(null);
        setSelectedSpaceId(null);
        setBuildingMenuShownId(null);
      }}
    >
      {({ hovered }: PressableState) => <>
        <View style={Style.row}>
          <PressableStyled
            onPress={() => {
              setExpandedBuildingId(buildingExpanded ? null : building.LocationId);
              setBuildingMenuShownId(null);
            }}
          >
            {Object.values(building.floors ?? {})?.length > 0 ? <DropdownIcon12
              color={styles.noChildrenCircle.borderColor}
              style={buildingExpanded ? Style.inverted : Style.upright}
            />
              : <View style={styles.noChildrenCircle} />}
          </PressableStyled>
          <Text
            style={[styles.title, hovered && styles.hoveredText]}
            ellipsizeMode='tail'
          >
            {building.Name}
          </Text>
        </View>
        {(canUpdateLocations || canDeleteLocations) && <PressableStyled
          style={styles.menuButton}
          onPress={() => {
            logEvent('LocationManagement', 'building_menu');
            setBuildingMenuShownId(menuShown ? null : building.LocationId);
          }}
          testID='lm-building-menu-btn'
        >
          <EllipsisIcon fill={Colors.light.hereworksDarkBlue} />
        </PressableStyled>}
      </>}
    </Pressable>
    {buildingExpanded && <View style={styles.floorList}>
      {Object.values(building.floors ?? {}).map((floor: Floor | null) => {
        if (!floor) return null;
        const floorExpanded: boolean = expandedFloorId === floor.LocationId;
        const floorSelected: boolean = !selectedSpace && !selectedArea
          && selectedFloor?.LocationId === floor.LocationId;
        return <View
          key={`menu_floor_item_${floor.LocationId}`}
          style={floorExpanded && floorSelected && [styles.selected, styles.selectedRegion]}
        >
          <View style={[
            styles.floorItem,
            floorSelected && !floorExpanded && [styles.selected, styles.selectedRegion],
          ]}>
            <PressableStyled
              onPress={() => {
                setExpandedFloorId(floorExpanded ? null : floor.LocationId);
                setBuildingMenuShownId(null);
              }}
            >
              {Object.values(floor?.areas ?? {}).length > 0 ? <DropdownIcon12
                color={styles.noChildrenCircle.borderColor}
                style={floorExpanded ? Style.inverted : Style.upright}
              />
                : <View style={styles.noChildrenCircle} />}
            </PressableStyled>
            <Pressable
              onPress={() => {
                setSelectedBuildingId(building?.LocationId);
                setSelectedFloorId(floor.LocationId);
                setSelectedAreaId(null);
                setSelectedSpaceId(null);
                setBuildingMenuShownId(null);
              }}>
              {({ hovered }: PressableState) => <Text
                style={[styles.floorTitle, hovered && styles.hoveredText]}
              >
                {floor.Name}
              </Text>}
            </Pressable>
          </View>
          {floorExpanded && <View style={styles.areasSection}>
            {Object.values(floor.areas ?? {}).map((area: Area | null) => {
              if (!area) return null;
              const areaExpanded: boolean = expandedAreaId === area.LocationId;
              const areaSelected: boolean = !selectedSpace
                && selectedArea?.LocationId === area.LocationId;
              return <View
                key={`menu_area_item${area.LocationId}`}
                style={areaExpanded && areaSelected && [styles.selected, styles.selectedRegion]}
              >
                <View style={[
                  styles.floorItem,
                  areaSelected && !areaExpanded && [styles.selected, styles.selectedRegion],
                ]}>
                  <PressableStyled
                    onPress={() => {
                      setExpandedAreaId(areaExpanded ? null : area.LocationId);
                      setBuildingMenuShownId(null);
                    }}
                  >
                    {Object.values(area?.spaces ?? {})?.length > 0 ? <DropdownIcon12
                      color={styles.noChildrenCircle.borderColor}
                      style={areaExpanded ? Style.inverted : Style.upright}
                    />
                      : <View style={styles.noChildrenCircle} />}
                  </PressableStyled>
                  <Pressable
                    onPress={() => {
                      setSelectedBuildingId(building.LocationId);
                      setSelectedFloorId(floor.LocationId);
                      setSelectedAreaId(area.LocationId);
                      setSelectedSpaceId(null);
                    }}
                  >
                    {({ hovered }: PressableState) => <View style={styles.areaTitleSection}>
                      <Text style={[styles.floorTitle, hovered && styles.hoveredText]}>
                        {area.Name}
                      </Text>
                      {area.LocationSubtype?.length > 0
                        && LocationSubtypeTitles[area.LocationSubtype]?.length > 0
                        && <Text style={[styles.label, hovered && styles.hoveredText]}>
                          {LocationSubtypeTitles[area.LocationSubtype]}
                        </Text>}
                    </View>}
                  </Pressable>
                </View>
                {areaExpanded && <View style={styles.spacesSection}>
                  {Object.values(area?.spaces ?? {}).map((space: Space | null) => {
                    if (!space) return null;
                    const spaceSelected: boolean = selectedSpace?.LocationId === space.LocationId;
                    return <Pressable
                      key={`menu_space_item_${space.LocationId}`}
                      onPress={() => {
                        setSelectedBuildingId(building?.LocationId);
                        setSelectedFloorId(floor.LocationId);
                        setSelectedAreaId(area.LocationId);
                        setSelectedSpaceId(space.LocationId);
                      }}
                      style={[
                        styles.floorItem, styles.spaceItem,
                        spaceSelected && styles.selected, spaceSelected && styles.selectedRegion,
                      ]}
                    >
                      {({ hovered }: PressableState) => <View style={styles.areaTitleSection}>
                        <Text style={[styles.floorTitle, hovered && styles.hoveredText]}>
                          {space.Name}
                        </Text>
                        {space.LocationSubtype?.length > 0
                          && LocationSubtypeTitles[space.LocationSubtype]?.length > 0
                          && <Text style={[styles.label, hovered && styles.hoveredText]}>
                            {LocationSubtypeTitles[space.LocationSubtype]}
                          </Text>}
                      </View>}
                    </Pressable>;
                  })}
                </View>}
              </View>;
            })}
          </View>}
        </View>;
      })
      }
    </View>}
    {menuShown && <View
      style={styles.dropdownView}
      testID='edit-delete-sub-menu'
    >
      {canUpdateLocations && <PressableStyled
        style={styles.option}
        onPress={onEditPress}
        testID='edit-building-sub-menu-button'
      >
        <Text style={styles.title}>{'Edit'}</Text>
      </PressableStyled>}
      {canDeleteLocations && <PressableStyled
        style={styles.option}
        onPress={onDeletePress}
        testID='delete-building-sub-menu-button'
      >
        <Text style={styles.title}>{'Delete'}</Text>
      </PressableStyled>}
    </View>}
    <AddBuildingModal
      {...{
        building,
        showScreenAlert,
        addBuildingModalShown,
        hideAddBuildingModal,
        editBuilding,
        checkIfNameUnique,
      }}
    />
  </View>;
};

const styles = StyleSheet.create({
  container: {
    alignSelf: 'stretch',
    borderRadius: 8,
    marginHorizontal: 12,
  },
  selected: {
    backgroundColor: Colors.light.hereworksWhite300,
    borderRadius: 8,
  },
  selectedRegion: {
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
  },
  header: {
    paddingVertical: 16,
    paddingHorizontal: 12,
  },
  title: {
    fontSize: 14,
    lineHeight: 14,
    marginStart: 4,
    marginEnd: 6,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack500,
    flex: 1,
  },
  floorList: {
    marginStart: 32,
    marginEnd: 12,
    paddingVertical: 5,
  },
  floorItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 2,
    padding: 4,
  },
  floorTitle: {
    fontSize: 12,
    lineHeight: 12,
    fontFamily: 'Roboto_500Medium',
    color: Colors.light.hereworksBlack500,
  },
  areaTitleSection: {
    marginVertical: 4,
    marginHorizontal: 4,
  },
  areasSection: {
    marginStart: 24,
  },
  spacesSection: {
    marginStart: 32,
  },
  dropdownView: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    position: 'absolute',
    end: 48,
    backgroundColor: '#FFF',
    zIndex: 2,
  },
  option: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    alignSelf: 'stretch',
  },
  noChildrenCircle: {
    width: 10,
    height: 10,
    borderRadius: 5,
    borderWidth: 2,
    borderColor: Colors.light.hereworksBlue500,
    marginHorizontal: 7,
  },
  menuButton: {
    paddingHorizontal: 6,
    paddingVertical: 4,
  },
  spaceItem: {
    paddingStart: 10,
  },
  label: {
    fontSize: 10,
    lineHeight: 14,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  hoveredText: {
    color: Colors.light.hereworksBlue500,
  },
});

export default BuildingMenuItem;
